import React from 'react';

import { CountryCode, getCountryCallingCode } from 'libphonenumber-js';

import { createClassName } from 'utils/createClassName';
import { setCustomTimeout } from 'utils/setCustomTimeout';
import {
  PhoneCountry,
  countDigits,
  detectDefaultCountry,
  formatPhoneNumber,
  getPhoneCountries,
  offsetAfterNthDigit,
  phoneValueToNationalDigits,
} from 'utils/phoneNumberUtils';

import './phoneFieldStyles.scss';

type Props = {
  value: string;
  onChange(nextValue: string): void;
  name?: string;
  disabled?: boolean;
  readOnly?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
  locale?: string;
  initialCountry?: string;
  countrySearchPlaceholderText?: string;
  countryNotFoundText?: string;
};

export const PhoneFieldComponent = (props: Props) => {
  const {
    value,
    onChange,
    name,
    disabled,
    readOnly,
    invalid,
    autoFocus,
    locale = 'ru',
    initialCountry,
    countrySearchPlaceholderText = 'Search country',
    countryNotFoundText = 'Country not found',
  } = props;

  const rootRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const [iso2, setIso2] = React.useState<null | CountryCode>(() => {
    const detected = initialCountry || detectDefaultCountry();
    return detected ? (detected as CountryCode) : null;
  });

  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [filterText, setFilterText] = React.useState('');

  const countries = React.useMemo(() => getPhoneCountries(locale), [locale]);

  const selectedCountry = React.useMemo(
    () =>
      countries.find((country) => country.iso2 === iso2) as
        | PhoneCountry
        | undefined,
    [countries, iso2]
  );

  const filteredCountries = React.useMemo(() => {
    const query = filterText.trim().toLowerCase();

    if (!query) return countries;

    return countries.filter(
      (country) =>
        country.name.toLowerCase().includes(query) ||
        country.dialCode.includes(query) ||
        country.iso2.toLowerCase().includes(query)
    );
  }, [countries, filterText]);

  const createPhoneFieldClassName = createClassName('phoneField');

  const applyDisplayValue = React.useCallback((nextValue: string) => {
    const input = inputRef.current;

    if (input && input.value !== nextValue) {
      input.value = nextValue;
    }
  }, []);

  React.useEffect(() => {
    if (!iso2) return;

    const nationalDigits = phoneValueToNationalDigits(value, iso2);
    applyDisplayValue(formatPhoneNumber(iso2, nationalDigits));
  }, [applyDisplayValue, iso2, value]);

  React.useEffect(() => {
    if (!autoFocus) return;

    return setCustomTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }, [autoFocus]);

  React.useEffect(() => {
    if (!isDropdownOpen) return;

    const onOutsideClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    window.addEventListener('click', onOutsideClick);

    return () => window.removeEventListener('click', onOutsideClick);
  }, [isDropdownOpen]);

  const selectCountry = React.useCallback(
    (nextIso2: string) => {
      if (nextIso2 !== iso2) {
        const nextCountryCode = nextIso2 as CountryCode;

        setIso2(nextCountryCode);
        applyDisplayValue('');
        onChange(`+${getCountryCallingCode(nextCountryCode)}`);
      }

      setFilterText('');
      setIsDropdownOpen(false);
      inputRef.current?.focus();
    },
    [applyDisplayValue, iso2, onChange]
  );

  const toggleDropdown = React.useCallback(() => {
    setIsDropdownOpen((prev) => !prev);
  }, []);

  const onNumberInput = React.useCallback(
    (event: Event) => {
      if (!iso2) return;

      const input = event.currentTarget as HTMLInputElement;
      const digitsBeforeCaret = countDigits(
        input.value.slice(0, input.selectionStart ?? input.value.length)
      );

      const nationalDigits = phoneValueToNationalDigits(input.value, iso2);
      const displayValue = formatPhoneNumber(iso2, nationalDigits);

      input.value = displayValue;
      input.setSelectionRange(
        offsetAfterNthDigit(displayValue, digitsBeforeCaret),
        offsetAfterNthDigit(displayValue, digitsBeforeCaret)
      );

      onChange(`+${getCountryCallingCode(iso2)}${nationalDigits}`);
    },
    [iso2, onChange]
  );

  React.useEffect(() => {
    const input = inputRef.current;

    if (!input) return;

    input.addEventListener('input', onNumberInput);

    return () => input.removeEventListener('input', onNumberInput);
  }, [onNumberInput]);

  const onFilterKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        inputRef.current?.focus();
        setIsDropdownOpen(false);
      } else if (event.key === 'Enter') {
        event.preventDefault();

        const first = filteredCountries[0];
        if (first) {
          selectCountry(first.iso2);
        }
      }
    },
    [filteredCountries, selectCountry]
  );

  return (
    <div className={createPhoneFieldClassName()} ref={rootRef}>
      <div className={createPhoneFieldClassName('group')}>
        <button
          type="button"
          className={createPhoneFieldClassName('countryButton')}
          disabled={!selectedCountry || disabled}
          aria-haspopup="listbox"
          aria-expanded={isDropdownOpen}
          onClick={toggleDropdown}
        >
          <span className={createPhoneFieldClassName('flag')}>
            {selectedCountry ? selectedCountry.flag : ''}
          </span>
          <span className={createPhoneFieldClassName('code')}>
            {selectedCountry ? `+${selectedCountry.dialCode}` : ''}
          </span>
        </button>

        <input
          ref={inputRef}
          type="tel"
          name={name}
          className={createPhoneFieldClassName('numberInput')}
          autoComplete="tel"
          inputMode="numeric"
          disabled={disabled}
          readOnly={readOnly}
          data-invalid={invalid}
        />

        {isDropdownOpen && (
          <div className={createPhoneFieldClassName('dropdown')}>
            <input
              type="text"
              className={createPhoneFieldClassName('filter')}
              placeholder={countrySearchPlaceholderText}
              value={filterText}
              autoFocus
              onChange={(event) => setFilterText(event.target.value)}
              onKeyDown={onFilterKeyDown}
            />

            <div className={createPhoneFieldClassName('list')} role="listbox">
              {filteredCountries.length === 0 ? (
                <div className={createPhoneFieldClassName('empty')}>
                  {countryNotFoundText}
                </div>
              ) : (
                filteredCountries.map((country) => (
                  <div
                    key={country.iso2}
                    className={createPhoneFieldClassName('item', {
                      active: country.iso2 === iso2,
                    })}
                    role="option"
                    aria-selected={country.iso2 === iso2}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={(event) => {
                      event.preventDefault();
                      selectCountry(country.iso2);
                    }}
                  >
                    <span className={createPhoneFieldClassName('flag')}>
                      {country.flag}
                    </span>
                    <span className={createPhoneFieldClassName('name')}>
                      {country.name}
                    </span>
                    <span className={createPhoneFieldClassName('code')}>
                      +{country.dialCode}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
